// 2026/9 入座制改版：群組／區域／ipg 碼表與 getUserInfo 清單解析。
// 純函式模組，不 import 其他專案檔案。
// 規格：docs/superpowers/2026-09-28-seat-only-revamp-design.md

export const SEAT_GROUPS = {
    JP: { key: 'JP', listField: 'JP_list' },
    Fish: { key: 'Fish', listField: 'Fish_list' },
    Slot: { key: 'Slot', listField: 'Slot_list' },
    Mult: { key: 'Mult', listField: 'Mult_list' },
};

// Groups API 的 grp 代碼 → 群組 key（鱼机 D／老虎机 C／押分机 B）
export const SEAT_GROUP_BY_GRP = { D: 'Fish', C: 'Slot', B: 'Mult' };

// 反查表：群組 key → Groups API grp 代碼（JP 是前端虛擬 tab，不在此表）
export const GRP_BY_SEAT_GROUP = Object.fromEntries(
    Object.entries(SEAT_GROUP_BY_GRP).map(([grp, key]) => [key, grp])
);

// 區域字樣為客戶指定的固定代碼（2026-10-02），各語系相同，不走 i18n
export const REGIONS = [
    { key: 'casual', label: 'BET=S', color: '#1e6fd9' },
    { key: 'main', label: 'BET=M', color: '#1f9d55' },
    { key: 'high', label: 'BET=L', color: '#d93025' },
    { key: 'vip', label: 'BET=VIP', color: '#7b2ff2' },
];

// getSeats 的 ipg 第一碼：群組 × 區域（Casual/Main/High/VIP 順序）
const IPG_REGION_CODES = {
    JP: ['H', 'I', 'J', 'K'],
    Fish: ['C', 'D', 'E', 'F'],
    Slot: ['S', 'T', 'U', 'V'],
    Mult: ['M', 'N', 'O', 'P'],
};

export function getSeatList(user, groupKey) {
    const field = SEAT_GROUPS[groupKey]?.listField;
    if (!field || !user) return null;
    // generalFetch 的 lowerCaseObjectKeys() 會把回應 key 首字母轉小寫
    // （JP_list→jP_list、Fish_list→fish_list），取值需兩種都認
    const lowerFirst = field.charAt(0).toLowerCase() + field.slice(1);
    const list = user[field] ?? user[lowerFirst];
    return Array.isArray(list) && list.length > 0 ? list : null;
}

export function parseSeatGame(game) {
    if (!Array.isArray(game)) return null;
    const base = { code: game[0], id: game[1], nameEN: game[2], nameCN: game[3] };
    if (game.length === 12) {
        return {
            ...base,
            legacy: false,
            regions: REGIONS.map((r, i) => ({
                ...r,
                used: Number(game[4 + i * 2]) || 0,
                total: Number(game[5 + i * 2]) || 0,
            })),
        };
    }
    if (game.length === 6) {
        return { ...base, legacy: true, used: Number(game[4]) || 0, total: Number(game[5]) || 0 };
    }
    return null;
}

export function visibleRegions(parsed) {
    if (!parsed || parsed.legacy || !parsed.regions) return [];
    return parsed.regions.filter((r) => r.total > 0);
}

export function buildIpg(groupKey, regionKey, gameCode, page) {
    const codes = IPG_REGION_CODES[groupKey];
    const idx = REGIONS.findIndex((r) => r.key === regionKey);
    if (!codes || idx === -1) return null;
    return `${codes[idx]}${gameCode}${page}`;
}

export function findSeatGame(user, groupKey, gameCode) {
    const list = getSeatList(user, groupKey);
    return list?.find((g) => g[0] === gameCode) ?? null;
}

/**
 * 反解 getUserInfo 的 seat_cacc（玩家目前座位帳號）→ 完整座位索引，
 * 供「一進站回到原座位」使用。
 * 格式：{站台前綴}{區域群組碼 1 字母}{遊戲碼 1 字母}{座位號 4 碼}，
 * 例 TT06IA0001＝JP×Main、遊戲 A、1 號座。由右往左解析，前綴長度不拘。
 * 區域碼僅認 16 個字母碼；數字碼（0~3 新組，2026-11 尚未定義清單欄位）
 * 與未知碼一律回 null，呼叫端安靜跳過。
 */
export function decodeSeatCacc(cacc, pgc = 20) {
    if (typeof cacc !== 'string') return null;
    const m = cacc.match(/([A-Z])([A-Z])(\d{4})$/);
    if (!m) return null;
    const [, regionCode, gameCode, seatStr] = m;
    for (const [groupKey, codes] of Object.entries(IPG_REGION_CODES)) {
        const idx = codes.indexOf(regionCode);
        if (idx !== -1) {
            const seatNo = parseInt(seatStr, 10);
            return {
                groupKey,
                regionKey: REGIONS[idx].key,
                gameCode,
                seatNo,
                page: Math.max(1, Math.ceil(seatNo / pgc)),
            };
        }
    }
    return null;
}
